import { useState } from 'react'
import {
  Box,
  HStack,
  VStack,
  Text,
  Icon,
  Select,
  Input,
  Button,
  Badge,
  Tooltip,
  useToast,
  SimpleGrid,
  FormControl,
  FormLabel,
} from '@chakra-ui/react'
import { LuDatabase, LuDownload, LuInfo } from 'react-icons/lu'
import {
  useDatasetFacets,
  downloadBlotDataset,
  type DatasetExportFilters,
} from '../../api/api_hooks/admin/pilots'

/** Human label for the known detection profiles. */
const PROFILE_LABEL: Record<string, string> = {
  'chu-nancy': 'CHU Nancy',
  'chu-amiens': 'CHU Amiens',
  aphp: 'AP-HP',
  default: 'Défaut',
}

interface BlotDatasetExportProps {
  /** Controlled filter state (shared with the pilots table so the CHU/period
   *  selection also narrows the activity list below). */
  filters: DatasetExportFilters
  onChange: (filters: DatasetExportFilters) => void
}

/**
 * Admin tool — export accumulated production cases as a Label Studio ZIP
 * (raw scans + stored strip boxes as pre-annotations) to correct and re-train
 * the detection model. Filters by pathology, CHU detection profile and period —
 * the same filters also drive the pilots activity table below.
 */
export default function BlotDatasetExport({ filters, onChange }: BlotDatasetExportProps) {
  const { data: facets, isLoading } = useDatasetFacets(filters)
  const toast = useToast()
  const [downloading, setDownloading] = useState(false)
  const hasFilters = Object.values(filters).some(Boolean)

  const set = (patch: Partial<DatasetExportFilters>) => onChange({ ...filters, ...patch })

  const handleExport = async () => {
    setDownloading(true)
    try {
      await downloadBlotDataset(filters)
      toast({
        title: 'Export généré',
        description: 'Le ZIP Label Studio a été téléchargé.',
        status: 'success',
        duration: 4000,
        position: 'top',
        isClosable: true,
      })
    } catch (e) {
      toast({
        title: "Échec de l'export",
        description: (e as Error).message,
        status: 'error',
        duration: 6000,
        position: 'top',
        isClosable: true,
      })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100" overflow="hidden">
      <HStack px={5} py={4} borderBottom="1px solid" borderColor="gray.100" spacing={3}>
        <Box
          w="38px"
          h="38px"
          borderRadius="lg"
          bgGradient="linear(135deg, #7c3aed, #22d3ee)"
          display="grid"
          sx={{ placeItems: 'center' }}
          boxShadow="0 4px 12px -3px rgba(124,58,237,0.45)"
        >
          <Icon as={LuDatabase} boxSize={4.5} color="white" />
        </Box>
        <VStack align="start" spacing={0} flex={1}>
          <HStack spacing={2}>
            <Text fontSize="sm" fontWeight="700" color="gray.800">Export dataset Label Studio</Text>
            {facets && (
              <Badge
                colorScheme={facets.matching === 0 ? 'red' : 'purple'}
                variant="subtle"
                fontSize="2xs"
              >
                {facets.matching === facets.total
                  ? `${facets.total} cas`
                  : `${facets.matching} cas sur ${facets.total}`}
              </Badge>
            )}
          </HStack>
          <Text fontSize="xs" color="gray.500">
            Scans bruts + boxes détectées (pré-annotations) pour ré-entraîner le modèle
          </Text>
        </VStack>
        <Tooltip
          hasArrow
          label="Le ZIP contient images/ + tasks.json (pré-annotations 'strip' orientées, telles que validées par le biologiste) + un README d'import. Les PDF sont rasterisés à 300 DPI, les TIFF convertis en PNG."
          placement="left"
        >
          <span><Icon as={LuInfo} color="gray.400" boxSize={4} /></span>
        </Tooltip>
      </HStack>

      <Box px={5} py={4}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
          <FormControl>
            <FormLabel fontSize="2xs" color="gray.500" textTransform="uppercase" mb={1}>Pathologie</FormLabel>
            <Select
              size="sm"
              placeholder="Toutes"
              isDisabled={isLoading}
              value={filters.pathologyId ?? ''}
              onChange={(e) => set({ pathologyId: e.target.value || undefined })}
            >
              {facets?.pathologies
                .filter((p) => p.id)
                .map((p) => (
                  <option key={p.id} value={p.id as string}>
                    {p.name} ({p.count})
                  </option>
                ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="2xs" color="gray.500" textTransform="uppercase" mb={1}>CHU / profil</FormLabel>
            <Select
              size="sm"
              placeholder="Tous"
              isDisabled={isLoading}
              value={filters.detectionProfile ?? ''}
              onChange={(e) => set({ detectionProfile: e.target.value || undefined })}
            >
              {facets?.profiles.map((p) => (
                <option key={p.profile} value={p.profile}>
                  {PROFILE_LABEL[p.profile] ?? p.profile} ({p.count})
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="2xs" color="gray.500" textTransform="uppercase" mb={1}>Depuis</FormLabel>
            <Input
              size="sm"
              type="date"
              value={filters.from ?? ''}
              onChange={(e) => set({ from: e.target.value || undefined })}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="2xs" color="gray.500" textTransform="uppercase" mb={1}>Jusqu'au</FormLabel>
            <Input
              size="sm"
              type="date"
              value={filters.to ?? ''}
              onChange={(e) => set({ to: e.target.value || undefined })}
            />
          </FormControl>
        </SimpleGrid>

        <HStack justify="space-between" mt={4}>
          <HStack spacing={2}>
            {hasFilters && (
              <Button size="xs" variant="ghost" color="gray.500" onClick={() => onChange({})}>
                Réinitialiser les filtres
              </Button>
            )}
          </HStack>
          <Button
            size="sm"
            colorScheme="purple"
            leftIcon={<Icon as={LuDownload} boxSize={4} />}
            onClick={handleExport}
            isLoading={downloading}
            loadingText="Génération…"
            isDisabled={facets?.matching === 0}
            title={facets?.matching === 0 ? 'Aucun cas ne correspond aux filtres' : undefined}
          >
            {facets && facets.matching !== facets.total
              ? `Exporter ${facets.matching} cas`
              : 'Exporter le ZIP'}
          </Button>
        </HStack>
      </Box>
    </Box>
  )
}
